import React, { useState, useEffect, useMemo } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';

interface TradingHistory {
  id: number;
  symbol: string;
  tradeType: 'LONG' | 'SHORT' | 'CLOSE';
  positionId: string | null;
  closeOrderId: string | null;
  entryPrice: number | null;
  exitPrice: number | null;
  amount: number;
  pnl: number;
  fees: number | null;
  exitReason: string | null;
  status: 'OPEN' | 'CLOSED' | 'CANCELLED';
  createdAt: string;
  closedAt: string | null;
}

interface Discrepancy {
  recordId: number;
  symbol: string;
  positionId: string | null;
  closeOrderId: string | null;
  field: string;
  databaseValue: number | null;
  kuCoinValue: number | null;
  difference: number;
  percentDifference: number;
}

interface ReconciliationResult {
  history: TradingHistory[];
  discrepancies: Discrepancy[];
  checkedCount: number;
  errorCount: number;
}

interface TradingStrategy {
  id: number;
  name: string;
  strategyType: string;
  status: string;
}

interface ReconciliationPanelProps {
  strategyId: number | null;
}

const ReconciliationPanel: React.FC<ReconciliationPanelProps> = ({ strategyId: propStrategyId }) => {
  const adminApiService = useAdminApiService();
  const [strategies, setStrategies] = useState<TradingStrategy[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(propStrategyId);
  const [history, setHistory] = useState<TradingHistory[]>([]);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [reconciling, setReconciling] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [syncingHistory, setSyncingHistory] = useState(false);
  const [fixingHedgePositions, setFixingHedgePositions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedCount, setCheckedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [backfillResult, setBackfillResult] = useState<{foundCount: number, updatedCount: number, errorCount: number} | null>(null);
  const [historySyncResult, setHistorySyncResult] = useState<{
    checkedCount: number;
    matchedCount: number;
    updatedCount: number;
    errorCount: number;
    dedupedCount?: number;
    importedCount?: number;
    errors?: string[];
  } | null>(null);
  const [hedgePositionsFixResult, setHedgePositionsFixResult] = useState<{checkedCount: number, fixedCount: number, errorCount: number} | null>(null);
  const [sortColumn, setSortColumn] = useState<'type' | 'symbol' | 'status' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Separate discrepancies into categories
  const missingCloseOrderIdDiscrepancies = discrepancies.filter(d =>
    d.field === 'MISSING_CLOSE_ORDER_ID' || d.field === 'INVALID_CLOSE_ORDER_ID'
  );
  const exitPriceFetchFailedDiscrepancies = discrepancies.filter(d =>
    d.field === 'EXIT_PRICE_FETCH_FAILED'
  );
  const missingDataDiscrepancies = [
    ...missingCloseOrderIdDiscrepancies,
    ...exitPriceFetchFailedDiscrepancies,
  ];
  const actualDiscrepancies = discrepancies.filter(d => 
    d.field !== 'MISSING_CLOSE_ORDER_ID' && 
    d.field !== 'EXIT_PRICE_FETCH_FAILED' &&
    d.field !== 'INVALID_CLOSE_ORDER_ID'
  );

  const recordsMissingCloseOrderId = useMemo(
    () => history.filter(r => !r.closeOrderId || r.closeOrderId === 'N/A').length,
    [history]
  );

  // Sort history based on selected column
  const sortedHistory = useMemo(() => {
    if (!sortColumn) return history;
    
    return [...history].sort((a, b) => {
      let aValue: string;
      let bValue: string;
      
      switch (sortColumn) {
        case 'type':
          aValue = a.tradeType || '';
          bValue = b.tradeType || '';
          break;
        case 'symbol':
          aValue = a.symbol || '';
          bValue = b.symbol || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }
      
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [history, sortColumn, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (column: 'type' | 'symbol' | 'status') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: 'type' | 'symbol' | 'status') => {
    if (sortColumn !== column) {
      return <span style={{ marginLeft: '4px', color: '#9ca3af', fontSize: '10px' }}>↕</span>;
    }
    return (
      <span style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px', fontWeight: 600 }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Load strategies on mount
  useEffect(() => {
    let isMounted = true;
    const loadStrategies = async () => {
      setLoadingStrategies(true);
      try {
        const response = await adminApiService.getAllTradingStrategies();
        if (!isMounted) return;
        
        if (response.success && response.data) {
          setStrategies(response.data);
        } else {
          console.error('Failed to load strategies:', response.error);
          setError(response.error || 'Failed to load strategies');
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Failed to load strategies:', err);
        setError(err.message || 'Failed to load strategies');
      } finally {
        if (isMounted) {
          setLoadingStrategies(false);
        }
      }
    };
    loadStrategies();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update selected strategy when prop changes
  useEffect(() => {
    if (propStrategyId) {
      setSelectedStrategyId(propStrategyId);
    }
  }, [propStrategyId]);

  const loadHistory = async () => {
    if (!selectedStrategyId) {
      console.log('No strategy selected, skipping history load');
      return;
    }
    
    console.log('Loading history for strategy:', selectedStrategyId);
    setLoading(true);
    setError(null);
    try {
      const response = await adminApiService.getStrategyReconciliationData(selectedStrategyId);
      console.log('History response:', response);
      
      if (response.success && response.data) {
        console.log(`Loaded ${response.data.length} history records`);
        setHistory(response.data);
      } else {
        console.error('Failed to load history:', response.error);
        setError(response.error || 'Failed to load data');
      }
    } catch (err: any) {
      console.error('Error loading history:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStrategyId) {
      loadHistory();
    } else {
      setHistory([]);
      setDiscrepancies([]);
    }
  }, [selectedStrategyId]);

  const handleReconcile = async () => {
    console.log('handleReconcile called', { selectedStrategyId, historyLength: history.length });
    
    if (!selectedStrategyId) {
      console.warn('No strategy selected');
      setError('Please select a strategy first');
      return;
    }
    
    if (history.length === 0) {
      console.warn('No history loaded yet');
      setError('Please wait for trading history to load first');
      return;
    }
    
    setReconciling(true);
    setError(null);
    console.log('Starting reconciliation for strategy:', selectedStrategyId);
    
    try {
      const response = await adminApiService.reconcileStrategyWithKuCoin(selectedStrategyId);
      console.log('Reconciliation response:', response);
      
      if (response.success && response.data) {
        const result: ReconciliationResult = response.data;
        console.log('Reconciliation result:', result);
        setDiscrepancies(result.discrepancies || []);
        setCheckedCount(result.checkedCount || 0);
        setErrorCount(result.errorCount || 0);
        
        if (result.discrepancies && result.discrepancies.length > 0) {
          console.log(`Found ${result.discrepancies.length} discrepancies`);
        } else {
          console.log('No discrepancies found - all data matches KuCoin');
        }
      } else {
        console.error('Reconciliation failed:', response.error);
        setError(response.error || 'Failed to reconcile');
      }
    } catch (err: any) {
      console.error('Reconciliation error:', err);
      setError(err.message || 'An error occurred during reconciliation');
    } finally {
      setReconciling(false);
    }
  };

  const handleFix = async (discrepanciesToFix?: Discrepancy[]) => {
    const toFix = discrepanciesToFix || actualDiscrepancies;
    if (toFix.length === 0) return;
    
    setFixing(true);
    setError(null);
    try {
      const response = await adminApiService.fixReconciliationDiscrepancies(toFix);
      if (response.success) {
        // Reload data after fixing
        await loadHistory();
        // Re-run reconciliation to see if issues are resolved
        await handleReconcile();
      } else {
        setError(response.error || 'Failed to fix discrepancies');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setFixing(false);
    }
  };

  const handleSyncKuCoinHistory = async () => {
    if (!selectedStrategyId) {
      setError('Please select a strategy first');
      return;
    }
    setSyncingHistory(true);
    setError(null);
    setHistorySyncResult(null);
    try {
      const response = await adminApiService.syncClosedTradesFromKuCoinHistory(selectedStrategyId);
      if (response.success && response.data) {
        setHistorySyncResult({
          checkedCount: response.data.checkedCount ?? 0,
          matchedCount: response.data.matchedCount ?? 0,
          updatedCount: response.data.updatedCount ?? 0,
          errorCount: response.data.errorCount ?? 0,
          dedupedCount: response.data.dedupedCount ?? 0,
          importedCount: response.data.importedCount ?? 0,
          errors: response.data.errors,
        });
        await loadHistory();
        await handleReconcile();
      } else {
        setError(response.error || 'Failed to sync from KuCoin history');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during KuCoin history sync');
    } finally {
      setSyncingHistory(false);
    }
  };

  const handleBackfill = async () => {
    if (!selectedStrategyId) {
      setError('Please select a strategy first');
      return;
    }
    
    setBackfilling(true);
    setError(null);
    setBackfillResult(null);
    try {
      const response = await adminApiService.backfillMissingCloseOrderIds(selectedStrategyId);
      if (response.success && response.data) {
        setBackfillResult({
          foundCount: response.data.foundCount || 0,
          updatedCount: response.data.updatedCount || 0,
          errorCount: response.data.errorCount || 0
        });
        // Reload history to see updated close_order_id values
        await loadHistory();
        // Re-run reconciliation to see updated discrepancies
        await handleReconcile();
      } else {
        setError(response.error || 'Failed to backfill missing order IDs');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const handleFixHedgePositions = async () => {
    if (!selectedStrategyId) {
      setError('Please select a strategy first');
      return;
    }
    
    setFixingHedgePositions(true);
    setError(null);
    setHedgePositionsFixResult(null);
    try {
      const response = await adminApiService.fixHedgePositions(selectedStrategyId);
      if (response.success && response.data) {
        setHedgePositionsFixResult({
          checkedCount: response.data.checkedCount || 0,
          fixedCount: response.data.fixedCount || 0,
          errorCount: response.data.errorCount || 0
        });
        // Reload history to see updated status
        await loadHistory();
        // Re-run reconciliation to see updated discrepancies
        await handleReconcile();
      } else {
        setError(response.error || 'Failed to fix hedge positions');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during hedge positions fix');
    } finally {
      setFixingHedgePositions(false);
    }
  };

  // Show loading state while strategies are loading
  if (loadingStrategies && strategies.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: '#666' }}>Loading reconciliation panel...</div>
      </div>
    );
  }

  return (
    <>
      {/* Strategy Selector */}
      <div style={{
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: 600,
          color: '#374151'
        }}>
          Select Strategy:
        </label>
        <select
          value={selectedStrategyId || ''}
          onChange={(e) => setSelectedStrategyId(e.target.value ? Number(e.target.value) : null)}
          disabled={loadingStrategies}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: 'white',
            cursor: loadingStrategies ? 'not-allowed' : 'pointer'
          }}
        >
          <option value="">-- Select a Strategy --</option>
          {strategies.map((strategy) => (
            <option key={strategy.id} value={strategy.id}>
              {strategy.name} (ID: {strategy.id})
            </option>
          ))}
        </select>
        <p style={{ 
          marginTop: '8px', 
          fontSize: '12px', 
          color: '#6b7280',
          fontStyle: 'italic'
        }}>
          Select a strategy to view and reconcile its trading history with KuCoin data
        </p>
      </div>

      {!selectedStrategyId ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Please select a strategy to view reconciliation data
        </div>
      ) : (
        <>
          <div style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#1a202c' }}>
              Strategy Trading History Reconciliation
            </h3>

            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#991b1b',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              flexWrap: 'wrap',
              alignItems: 'center',
              border: '2px solid #e5e7eb'
            }}>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Button clicked', { 
                    selectedStrategyId, 
                    historyLength: history.length, 
                    reconciling, 
                    loading,
                    disabled: reconciling || loading || history.length === 0 || !selectedStrategyId
                  });
                  if (!selectedStrategyId) {
                    setError('Please select a strategy first');
                    return;
                  }
                  if (history.length === 0) {
                    setError('Please wait for trading history to load first');
                    return;
                  }
                  handleReconcile();
                }} 
                disabled={reconciling || loading || history.length === 0 || !selectedStrategyId}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (reconciling || loading || history.length === 0 || !selectedStrategyId) ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (reconciling || loading || history.length === 0 || !selectedStrategyId) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (reconciling || loading || history.length === 0 || !selectedStrategyId) ? 0.6 : 1,
                  boxShadow: (reconciling || loading || history.length === 0 || !selectedStrategyId) ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s'
                }}
                title={!selectedStrategyId ? 'Please select a strategy first' : history.length === 0 ? 'Waiting for history to load...' : 'Cross-check trading history with KuCoin API'}
              >
                {reconciling ? (
                  <>
                    <span>🔄</span>
                    <span>Cross-Checking with KuCoin...</span>
                  </>
                ) : (
                  <>
                    <span>🔍</span>
                    <span>Cross-Check with KuCoin</span>
                  </>
                )}
              </button>

              {/* Sync exit & PnL from KuCoin history-positions (fixes ticker vs fill mismatches e.g. BRETT) */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSyncKuCoinHistory();
                }}
                disabled={syncingHistory || loading || history.length === 0 || !selectedStrategyId}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (syncingHistory || loading || history.length === 0 || !selectedStrategyId) ? '#9ca3af' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (syncingHistory || loading || history.length === 0 || !selectedStrategyId) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (syncingHistory || loading || history.length === 0 || !selectedStrategyId) ? 0.6 : 1,
                  boxShadow: (syncingHistory || loading || history.length === 0 || !selectedStrategyId) ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  marginLeft: '12px'
                }}
                title="Copy closePrice, realised PnL and fees from KuCoin history-positions into trading_history (best fix for wrong exit/PnL when order-fill lookup fails)"
              >
                {syncingHistory ? (
                  <>
                    <span>🔄</span>
                    <span>Syncing from KuCoin History...</span>
                  </>
                ) : (
                  <>
                    <span>📊</span>
                    <span>Sync Exit &amp; P&amp;L from KuCoin History</span>
                  </>
                )}
              </button>

              {/* Backfill Missing Order IDs Button */}
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBackfill();
                }} 
                disabled={backfilling || !selectedStrategyId || recordsMissingCloseOrderId === 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (backfilling || !selectedStrategyId || recordsMissingCloseOrderId === 0) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (backfilling || !selectedStrategyId || recordsMissingCloseOrderId === 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (backfilling || !selectedStrategyId || recordsMissingCloseOrderId === 0) ? 0.6 : 1,
                  boxShadow: (backfilling || !selectedStrategyId || recordsMissingCloseOrderId === 0) ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  marginLeft: '12px'
                }}
                title={!selectedStrategyId ? 'Please select a strategy first' : recordsMissingCloseOrderId === 0 ? 'All records already have a close order ID' : 'Recover missing close_order_id from KuCoin orders (only rows with null ID in DB)'}
              >
                {backfilling ? (
                  <>
                    <span>🔄</span>
                    <span>Backfilling...</span>
                  </>
                ) : (
                  <>
                    <span>🔧</span>
                    <span>Backfill Missing Order IDs ({recordsMissingCloseOrderId})</span>
                  </>
                )}
              </button>

              {/* Fix Hedge Positions Button */}
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFixHedgePositions();
                }} 
                disabled={fixingHedgePositions || !selectedStrategyId}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (fixingHedgePositions || !selectedStrategyId) ? '#9ca3af' : '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (fixingHedgePositions || !selectedStrategyId) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (fixingHedgePositions || !selectedStrategyId) ? 0.6 : 1,
                  boxShadow: (fixingHedgePositions || !selectedStrategyId) ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  marginLeft: '12px'
                }}
                title={!selectedStrategyId ? 'Please select a strategy first' : 'Check hedge_positions marked as OPEN and mark them as CLOSED if they are closed on Kucoin'}
              >
                {fixingHedgePositions ? (
                  <>
                    <span>🔄</span>
                    <span>Fixing Hedge Positions...</span>
                  </>
                ) : (
                  <>
                    <span>🔍</span>
                    <span>Fix Closed Hedge Positions</span>
                  </>
                )}
              </button>
              
              {/* Status indicator */}
              {selectedStrategyId && (
                <div style={{ 
                  fontSize: '13px', 
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  {loading && <span>⏳ Loading history...</span>}
                  {!loading && history.length === 0 && <span>⚠️ No trading history found</span>}
                  {!loading && history.length > 0 && <span>✅ {history.length} records loaded - Ready to reconcile</span>}
                </div>
              )}
              
              {!selectedStrategyId && (
                <div style={{ 
                  fontSize: '13px', 
                  color: '#dc2626',
                  padding: '8px 12px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '6px',
                  border: '1px solid #fecaca'
                }}>
                  ⚠️ Please select a strategy from the dropdown above
                </div>
              )}

              {/* Hedge Positions Fix Results Display */}
              {hedgePositionsFixResult && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: hedgePositionsFixResult.fixedCount > 0 ? '#f0fdf4' : '#fefce8',
                  border: `1px solid ${hedgePositionsFixResult.fixedCount > 0 ? '#86efac' : '#fde047'}`,
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: '#166534',
                    marginBottom: '8px'
                  }}>
                    {hedgePositionsFixResult.fixedCount > 0 ? '✅' : 'ℹ️'} Hedge Positions Fix Complete
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                    <div>Checked: <strong>{hedgePositionsFixResult.checkedCount}</strong> positions</div>
                    <div>Fixed: <strong>{hedgePositionsFixResult.fixedCount}</strong> positions (marked as CLOSED)</div>
                    {hedgePositionsFixResult.errorCount > 0 && (
                      <div style={{ color: '#dc2626' }}>Errors: <strong>{hedgePositionsFixResult.errorCount}</strong></div>
                    )}
                  </div>
                </div>
              )}

              {historySyncResult && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: historySyncResult.updatedCount > 0 ? '#f0fdf4' : '#eff6ff',
                  border: `1px solid ${historySyncResult.updatedCount > 0 ? '#86efac' : '#bfdbfe'}`,
                  borderRadius: '8px',
                  width: '100%'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#1e40af', fontSize: '16px', fontWeight: 600 }}>
                    {historySyncResult.updatedCount > 0 ? '✅' : 'ℹ️'} KuCoin History Sync
                  </h4>
                  <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                    <div>Checked: <strong>{historySyncResult.checkedCount}</strong> closed trades</div>
                    <div>Matched on KuCoin: <strong>{historySyncResult.matchedCount}</strong></div>
                    <div>Updated in DB: <strong>{historySyncResult.updatedCount}</strong></div>
                    {(historySyncResult.dedupedCount ?? 0) > 0 && (
                      <div style={{ color: '#7c3aed' }}>Duplicate CLOSE rows removed: <strong>{historySyncResult.dedupedCount}</strong></div>
                    )}
                    {(historySyncResult.importedCount ?? 0) > 0 && (
                      <div style={{ color: '#059669' }}>Missing closes imported from KuCoin: <strong>{historySyncResult.importedCount}</strong></div>
                    )}
                    {historySyncResult.errorCount > 0 && (
                      <div style={{ color: '#dc2626' }}>Unmatched / errors: <strong>{historySyncResult.errorCount}</strong></div>
                    )}
                  </div>
                  {historySyncResult.updatedCount > 0 && (
                    <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#065f46' }}>
                      Rows now use <code>RECONCILIATION_KUCOIN_HISTORY</code> with KuCoin close price and realised PnL.
                    </p>
                  )}
                </div>
              )}

              {/* Backfill Results Display */}
              {backfillResult && (
                <div style={{ 
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: backfillResult.updatedCount > 0 ? '#f0fdf4' : '#fffbeb',
                  border: `1px solid ${backfillResult.updatedCount > 0 ? '#bbf7d0' : '#fde68a'}`,
                  borderRadius: '8px'
                }}>
                  <h4 style={{ 
                    margin: '0 0 12px 0', 
                    color: backfillResult.updatedCount > 0 ? '#059669' : '#f59e0b',
                    fontSize: '16px',
                    fontWeight: 600
                  }}>
                    {backfillResult.updatedCount > 0 ? '✅' : 'ℹ️'} Backfill Results
                  </h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '12px',
                    fontSize: '14px'
                  }}>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Found</div>
                      <div style={{ fontWeight: 600, color: '#1e40af' }}>
                        {backfillResult.foundCount}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Updated</div>
                      <div style={{ fontWeight: 600, color: '#059669' }}>
                        {backfillResult.updatedCount}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Errors</div>
                      <div style={{ fontWeight: 600, color: backfillResult.errorCount > 0 ? '#dc2626' : '#059669' }}>
                        {backfillResult.errorCount}
                      </div>
                    </div>
                  </div>
                  {backfillResult.updatedCount > 0 && (
                    <div style={{ 
                      marginTop: '12px',
                      padding: '8px',
                      backgroundColor: '#d1fae5',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#065f46'
                    }}>
                      ✅ Successfully backfilled {backfillResult.updatedCount} record(s). Reconciliation has been re-run automatically.
                    </div>
                  )}
                  {backfillResult.updatedCount === 0 && backfillResult.foundCount === 0 && (
                    <div style={{ 
                      marginTop: '12px',
                      padding: '8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#92400e'
                    }}>
                      ℹ️ No close orders found in KuCoin API. This may be because:
                      <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                        <li>Records are older than 30 days (KuCoin may have purged the data)</li>
                        <li>Orders were not found in the ±1 hour window around close time</li>
                        <li>Symbol format mismatch between database and KuCoin</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

          {actualDiscrepancies.length > 0 && (
          <button 
            onClick={(e) => {
              e.preventDefault();
              if (!window.confirm(`Apply KuCoin values to ${actualDiscrepancies.length} discrepancy(ies) in the database?`)) {
                return;
              }
              handleFix(actualDiscrepancies);
            }} 
            disabled={fixing}
            style={{
              padding: '12px 24px',
              backgroundColor: fixing ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: fixing ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {fixing ? (
              <>
                <span>⚙️</span>
                <span>Fixing...</span>
              </>
            ) : (
              <>
                <span>✅</span>
                <span>Apply Fix to {actualDiscrepancies.length} Discrepancy(ies)</span>
              </>
            )}
          </button>
        )}
      </div>

            {/* Summary Stats */}
            <div style={{ 
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px'
      }}>
        <div style={{ 
          padding: '12px',
          backgroundColor: '#eff6ff',
          borderRadius: '6px',
          border: '1px solid #bfdbfe'
        }}>
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
            Total Records
          </div>
          <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e40af' }}>
            {history.length}
          </div>
        </div>
        <div style={{ 
          padding: '12px',
          backgroundColor: discrepancies.length > 0 ? '#fef2f2' : '#f0fdf4',
          borderRadius: '6px',
          border: `1px solid ${discrepancies.length > 0 ? '#fecaca' : '#bbf7d0'}`
        }}>
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
            Fixable discrepancies
          </div>
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 600, 
            color: actualDiscrepancies.length > 0 ? '#dc2626' : '#059669'
          }}>
            {actualDiscrepancies.length}
          </div>
          {missingDataDiscrepancies.length > 0 && (
            <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
              +{missingDataDiscrepancies.length} cannot auto-verify
            </div>
          )}
        </div>
        <div style={{ 
          padding: '12px',
          backgroundColor: '#fef3c7',
          borderRadius: '6px',
          border: '1px solid #fde68a'
        }}>
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
            Records with Close Order ID
          </div>
          <div style={{ fontSize: '20px', fontWeight: 600, color: '#92400e' }}>
            {history.filter(r => r.closeOrderId).length} / {history.length}
          </div>
        </div>
        {checkedCount > 0 && (
          <div style={{ 
            padding: '12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '6px',
            border: '1px solid #d1d5db'
          }}>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              Checked
            </div>
            <div style={{ fontSize: '20px', fontWeight: 600, color: '#374151' }}>
              {checkedCount}
            </div>
          </div>
        )}
      </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                Loading...
              </div>
            ) : (
              <>
                {/* Strategy Data Table */}
                <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
              Strategy Trading History
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Record ID</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                    <th 
                      style={{ 
                        padding: '8px', 
                        textAlign: 'left', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        backgroundColor: sortColumn === 'type' ? '#e5e7eb' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleSort('type')}
                      onMouseEnter={(e) => {
                        if (sortColumn !== 'type') {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortColumn !== 'type') {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      Type{renderSortIndicator('type')}
                    </th>
                    <th 
                      style={{ 
                        padding: '8px', 
                        textAlign: 'left', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        backgroundColor: sortColumn === 'symbol' ? '#e5e7eb' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleSort('symbol')}
                      onMouseEnter={(e) => {
                        if (sortColumn !== 'symbol') {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortColumn !== 'symbol') {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      Symbol{renderSortIndicator('symbol')}
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Position ID</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Close Order ID</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Entry Price</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Exit Price</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>PnL</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Fees</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Exit Reason</th>
                    <th 
                      style={{ 
                        padding: '8px', 
                        textAlign: 'left', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        backgroundColor: sortColumn === 'status' ? '#e5e7eb' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleSort('status')}
                      onMouseEnter={(e) => {
                        if (sortColumn !== 'status') {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortColumn !== 'status') {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      Status{renderSortIndicator('status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistory.map((record, index) => (
                    <tr 
                      key={record.id} 
                      style={{ 
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>
                        {record.id}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {record.closedAt 
                          ? new Date(record.closedAt).toLocaleString() 
                          : new Date(record.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          backgroundColor: record.tradeType === 'LONG' ? '#dbeafe' 
                            : record.tradeType === 'SHORT' ? '#fef3c7' 
                            : '#e0e7ff',
                          color: record.tradeType === 'LONG' ? '#1e40af' 
                            : record.tradeType === 'SHORT' ? '#92400e' 
                            : '#3730a3'
                        }}>
                          {record.tradeType}
                        </span>
                      </td>
                      <td style={{ padding: '8px', fontWeight: 600 }}>{record.symbol}</td>
                      <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                        {record.positionId || <span style={{ color: '#999' }}>N/A</span>}
                      </td>
                      <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                        {record.closeOrderId ? (
                          <span style={{ color: '#059669', fontWeight: 600 }}>
                            {record.closeOrderId}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {record.entryPrice ? `$${Number(record.entryPrice).toFixed(8)}` : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {record.exitPrice ? `$${Number(record.exitPrice).toFixed(8)}` : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                        ${Number(record.amount).toFixed(2)}
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        textAlign: 'right',
                        fontFamily: 'monospace',
                        color: Number(record.pnl) >= 0 ? '#059669' : '#dc2626',
                        fontWeight: 600
                      }}>
                        ${Number(record.pnl).toFixed(2)}
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        textAlign: 'right',
                        fontFamily: 'monospace',
                        color: '#6b7280',
                        fontSize: '11px'
                      }}>
                        {record.fees != null ? `$${Number(record.fees).toFixed(4)}` : '-'}
                      </td>
                      <td style={{ padding: '8px', fontSize: '11px' }}>
                        {record.exitReason || '-'}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          backgroundColor: record.status === 'CLOSED' ? '#d1fae5' 
                            : record.status === 'OPEN' ? '#dbeafe' 
                            : '#fee2e2',
                          color: record.status === 'CLOSED' ? '#065f46' 
                            : record.status === 'OPEN' ? '#1e40af' 
                            : '#991b1b'
                        }}>
                          {record.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

                {/* Discrepancies Tables */}
                {(missingDataDiscrepancies.length > 0 || actualDiscrepancies.length > 0) && (
                  <>
                    {missingDataDiscrepancies.length > 0 && (
                      <div style={{ 
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          color: '#f59e0b',
                          fontSize: '16px',
                          fontWeight: 600
                        }}>
                          ℹ️ {missingDataDiscrepancies.length} Historical Record(s) - Cannot Verify
                        </h4>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #fde68a',
                          borderRadius: '6px',
                          marginBottom: '12px',
                          fontSize: '13px',
                          color: '#92400e'
                        }}>
                          {missingCloseOrderIdDiscrepancies.length > 0 && (
                            <p style={{ margin: '0 0 8px 0' }}>
                              {missingCloseOrderIdDiscrepancies.length} record(s) are missing a valid{' '}
                              <code style={{ backgroundColor: '#fde68a', padding: '2px 4px', borderRadius: '3px' }}>close_order_id</code>
                              {' '}(common for older trades). Run <strong>Backfill Missing Order IDs</strong> first.
                            </p>
                          )}
                          {exitPriceFetchFailedDiscrepancies.length > 0 && (
                            <p style={{ margin: 0 }}>
                              {exitPriceFetchFailedDiscrepancies.length} record(s) have a{' '}
                              <code style={{ backgroundColor: '#fde68a', padding: '2px 4px', borderRadius: '3px' }}>close_order_id</code>
                              {' '}but KuCoin could not confirm the exit price (history match failed or order-fill API returned nothing).
                              Run <strong>Sync Exit &amp; P&amp;L from KuCoin History</strong> after deploy; if values already look correct, no DB change is required.
                            </p>
                          )}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#fef3c7', borderBottom: '2px solid #fde68a' }}>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Record ID</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Symbol</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Position ID</th>
                                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Exit Price (DB)</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Exit Reason</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Record Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {missingDataDiscrepancies.map((d, idx) => {
                                // Find the corresponding history record to get the date, status, and exit reason
                                const historyRecord = history.find(h => h.id === d.recordId);
                                const displayDate = historyRecord 
                                  ? (historyRecord.closedAt 
                                      ? new Date(historyRecord.closedAt).toLocaleString() 
                                      : new Date(historyRecord.createdAt).toLocaleString())
                                  : 'N/A';
                                const recordStatus = historyRecord?.status || 'N/A';
                                const exitReason = historyRecord?.exitReason || '-';
                                
                                return (
                                  <tr key={idx} style={{ borderBottom: '1px solid #fde68a', backgroundColor: idx % 2 === 0 ? 'white' : '#fffbeb' }}>
                                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{d.recordId}</td>
                                    <td style={{ padding: '8px', fontSize: '11px' }}>{displayDate}</td>
                                    <td style={{ padding: '8px', fontWeight: 600 }}>{d.symbol}</td>
                                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{d.positionId || 'N/A'}</td>
                                    <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                                      {d.databaseValue !== null ? `$${Number(d.databaseValue).toFixed(8)}` : 'N/A'}
                                    </td>
                                    <td style={{ padding: '8px', fontSize: '11px' }}>{exitReason}</td>
                                    <td style={{ padding: '8px' }}>
                                      <span style={{ 
                                        padding: '4px 8px', 
                                        backgroundColor: '#fef3c7', 
                                        color: '#92400e',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 500
                                      }}>
                                        Historical Record
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      {recordStatus !== 'N/A' ? (
                                        <span style={{
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          backgroundColor: recordStatus === 'CLOSED' ? '#d1fae5' 
                                            : recordStatus === 'OPEN' ? '#dbeafe' 
                                            : '#fee2e2',
                                          color: recordStatus === 'CLOSED' ? '#065f46' 
                                            : recordStatus === 'OPEN' ? '#1e40af' 
                                            : '#991b1b'
                                        }}>
                                          {recordStatus}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#999', fontSize: '11px' }}>N/A</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {actualDiscrepancies.length > 0 && (
                      <div style={{ 
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          color: '#dc2626',
                          fontSize: '16px',
                          fontWeight: 600
                        }}>
                          ⚠️ Found {actualDiscrepancies.length} Data Mismatch(es) - Can Be Fixed
                        </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fee2e2', borderBottom: '2px solid #fecaca' }}>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Record ID</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Symbol</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Position ID</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Close Order ID</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Field</th>
                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Database Value</th>
                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>KuCoin Value</th>
                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Difference</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Exit Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actualDiscrepancies.map((d, index) => {
                      // Find the corresponding record from history to get status and exitReason
                      const record = history.find(r => r.id === d.recordId);
                      return (
                        <tr 
                          key={d.recordId}
                          style={{ 
                            borderBottom: '1px solid #fecaca',
                            backgroundColor: index % 2 === 0 ? 'white' : '#fef2f2'
                          }}
                        >
                          <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                            {d.recordId}
                          </td>
                          <td style={{ padding: '8px', fontWeight: 600 }}>{d.symbol}</td>
                          <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                            {d.positionId || <span style={{ color: '#999' }}>N/A</span>}
                          </td>
                          <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                            {d.closeOrderId ? (
                              <span style={{ color: '#059669' }}>{d.closeOrderId}</span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </td>
                          <td style={{ padding: '8px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e'
                            }}>
                              {d.field}
                            </span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                            {d.databaseValue !== null 
                              ? (typeof d.databaseValue === 'number' 
                                ? `$${d.databaseValue.toFixed(8)}` 
                                : d.databaseValue)
                              : 'N/A'}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 600 }}>
                            {d.kuCoinValue !== null 
                              ? (typeof d.kuCoinValue === 'number' 
                                ? `$${d.kuCoinValue.toFixed(8)}` 
                                : d.kuCoinValue)
                              : 'N/A'}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            color: '#dc2626',
                            fontWeight: 600
                          }}>
                            {d.difference !== null 
                              ? (typeof d.difference === 'number' 
                                ? `$${d.difference.toFixed(8)}` 
                                : d.difference)
                              : 'N/A'}
                          </td>
                          <td style={{ padding: '8px' }}>
                            {record?.status ? (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: record.status === 'CLOSED' ? '#d1fae5' : 
                                               record.status === 'OPEN' ? '#dbeafe' : '#f3f4f6',
                                color: record.status === 'CLOSED' ? '#065f46' : 
                                       record.status === 'OPEN' ? '#1e40af' : '#374151'
                              }}>
                                {record.status}
                              </span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </td>
                          <td style={{ padding: '8px', fontSize: '11px' }}>
                            {record?.exitReason ? (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                backgroundColor: '#e0e7ff',
                                color: '#3730a3',
                                fontFamily: 'monospace'
                              }}>
                                {record.exitReason}
                              </span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ReconciliationPanel;
